import React from "react";
import { Button, InputNumber, Space, Switch } from "antd";
import { ColumnType } from "antd/lib/table";
import { isEmpty, isNil, map } from "lodash";
import { EMPTY } from "constants/common";
import { AiccTestAccount } from "types/model/aiccTestAccount";
import styles from "./TestAccount.module.scss";

interface ColumnHandlers {
  editingCustomerId?: string;
  quotaDraft?: number;
  onQuotaDraftChange: (quota?: number) => void;
  onEditQuota: (account: AiccTestAccount) => void;
  onSaveQuota: (account: AiccTestAccount) => void;
  onCancelQuota: () => void;
  onToggleAccess: (account: AiccTestAccount, hasAccess: boolean) => void;
  onDelete: (account: AiccTestAccount) => void;
  onViewSubUser: (subUserId: string) => void;
}

const getColumns = ({
  editingCustomerId,
  quotaDraft,
  onQuotaDraftChange,
  onEditQuota,
  onSaveQuota,
  onCancelQuota,
  onToggleAccess,
  onDelete,
  onViewSubUser,
}: ColumnHandlers): ColumnType<AiccTestAccount>[] => {
  const isEditingAny = !isNil(editingCustomerId);

  return [
    {
      title: "Email",
      dataIndex: "customerEmail",
      key: "customerEmail",
      width: 280,
      render: (_, { customerEmail }) => customerEmail || EMPTY,
    },
    {
      title: "Current Message Quota",
      dataIndex: "messageQuota",
      key: "messageQuota",
      width: 180,
      render: (_, account) =>
        account.customerId === editingCustomerId ? (
          <Space size={4}>
            <span>{`${account.messageUsage} / `}</span>
            <InputNumber
              min={1}
              precision={0}
              value={quotaDraft}
              className={styles.quotaInput}
              onChange={(value) => onQuotaDraftChange(value === null ? undefined : Number(value))}
            />
          </Space>
        ) : (
          `${account.messageUsage} / ${account.messageQuota}`
        ),
    },
    {
      title: "Preview Subuser",
      dataIndex: "subUsers",
      key: "subUsers",
      width: 240,
      render: (_, { subUsers }) =>
        isEmpty(subUsers) ? (
          EMPTY
        ) : (
          <div className={styles.subUsers}>
            {map(subUsers, ({ id, name }) => (
              <Button key={id} type="link" className={styles.subUserLink} onClick={() => onViewSubUser(id)}>
                {`${name} (${id})`}
              </Button>
            ))}
          </div>
        ),
    },
    {
      title: "Preview Access",
      dataIndex: "hasAccess",
      key: "hasAccess",
      width: 160,
      render: (_, account) => (
        <Switch
          checked={account.hasAccess}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          disabled={isEditingAny}
          onChange={(checked) => onToggleAccess(account, checked)}
        />
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 220,
      render: (_, account) => (
        <Space size={12}>
          {account.customerId === editingCustomerId ? (
            <>
              <Button type="link" className={styles.linkButton} onClick={() => onSaveQuota(account)}>
                Save
              </Button>
              <Button type="link" className={styles.linkButton} onClick={onCancelQuota}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                type="link"
                className={styles.linkButton}
                disabled={isEditingAny}
                onClick={() => onEditQuota(account)}
              >
                Edit Quota
              </Button>
              <Button
                type="link"
                className={styles.linkButton}
                disabled={isEditingAny}
                onClick={() => onDelete(account)}
              >
                Delete
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];
};

export default getColumns;
